import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify, { MESSAGES } from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import { UoMType, PricingUnitType } from '../../constants/masterData';
import {
  FormGrid,
  RequiredNote,
  SelectField,
  TextAreaField,
  TextField,
  focusFirstError,
  hasErrors,
  required,
  validateForm,
} from '../../Components/Form';

const PRODUCT_TYPE_SCHEMA = {
  productId: { label: 'Product', rules: [required()] },
  name: { label: 'Product type name', rules: [required()] },
};
const PRODUCT_TYPE_ORDER = ['productId', 'name'];

const ProductTypeList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [errors, setErrors] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalProductType, setModalProductType] = useState(false);
  const [productTypeFormData, setProductTypeFormData] = useState({
    productId: '',
    name: '',
    description: '',
    id: null,
  });

  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(
          `${api.API_URL}/product-type/${invoice.id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        notify.success('Product Type deleted successfully.');
        setDeleteModal(false);
        setInvoice(null);
        const filters = {
          productId: selectedProduct,
          searchText: search,
        };
        fetchData(currentPage, rowsPerPage, token, filters);
      } catch (err) {
        notify.apiError(err, 'delete product type');
      }
    }
  };

  const columns = [
    {
      id: 'name',
      name: 'Product Type Name',
      selector: row => row.name,
      sortable: true,
      width: '20%',
      cell: row => <span className='to-title'>{row.name || '-'}</span>,
    },
    {
      id: 'product',
      name: 'Product',
      selector: row => row.product?.name,
      sortable: true,
      width: '20%',
      cell: row =>
        row.product?.name ? (
          <span className='to-tag'>{row.product.name}</span>
        ) : (
          '-'
        ),
    },
    {
      id: 'uoM',
      name: 'UoM',
      selector: row => row.uoM,
      sortable: true,
      width: '10%',
      cell: row => row.uoM || '-',
    },
    {
      id: 'pricingUnit',
      name: 'Pricing Unit',
      selector: row => row.pricingUnit,
      sortable: true,
      width: '10%',
      cell: row => row.pricingUnit || '-',
    },
    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      sortable: true,
      width: '30%',
      cell: row => (
        <span className='pv-desc' title={row.description || ''}>
          {row.description || '-'}
        </span>
      ),
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      width: '10%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row.id)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Product Type';

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    const filters = {
      productId: selectedProduct,
      searchText: search,
    };
    fetchData(currentPage, rowsPerPage, token, filters);
  }, [currentPage, rowsPerPage, token, selectedProduct, search]);

  useEffect(() => {
    fetchProducts(token);
  }, [token]);

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const handleProductFilterChange = value => {
    setSelectedProduct(value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const clearAllFilters = () => {
    setFilterText('');
    setSearch('');
    setSelectedProduct('');
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const fetchData = async (currentPage, rowsPerPage, token, filters = {}) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    try {
      // Build query parameters
      const queryParams = new URLSearchParams({
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      });

      // Add filter parameters if they exist
      if (filters.productId) {
        queryParams.append('ProductId', filters.productId);
      }
      if (filters.searchText) {
        queryParams.append('Search', filters.searchText);
      }

      const url = `${api.API_URL}/product-type?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (reqId !== requestRef.current) return; // stale response

      // Handle different possible response structures
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      const totalItems =
        result?.data?.totalItems || result?.totalItems || items.length;

      setData({
        items: Array.isArray(items) ? items : [],
      });
      setTotalRows(totalItems);
    } catch (error) {
      if (reqId !== requestRef.current) return;
      notify.apiError(error, 'load data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  };

  const fetchProducts = async token => {
    try {
      const response = await fetch(`${api.API_URL}/product`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      setProducts(Array.isArray(items) ? items : []);
    } catch (error) {
      notify.apiError(error, 'load products');
      setProducts([]);
    }
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setErrors({});
    setProductTypeFormData({
      productId: '',
      name: '',
      description: '',
      id: null,
    });
    productTypeModal();
  };

  const handleUpdateModal = async (mid = null) => {
    setErrors({});
    await fetchProductTypeDetail(mid);
    productTypeModal();
  };

  const productTypeModal = () => {
    setModalProductType(!modalProductType);
  };

  const selectedFieldsProductType = {
    productId: productTypeFormData.productId,
    name: productTypeFormData.name,
    description: productTypeFormData.description,
    uoM: productTypeFormData.uoM,
    pricingUnit: productTypeFormData.pricingUnit,
  };

  const handleAddDataProductType = async () => {
    try {
      await axios.post(
        `${api.API_URL}/product-type`,
        selectedFieldsProductType,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success('Product Type added successfully.');
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      const filters = {
        productId: selectedProduct,
        searchText: search,
      };
      fetchData(currentPage, rowsPerPage, token, filters);
    }
  };

  const fetchProductTypeDetail = async mid => {
    try {
      const response = await axios.get(`${api.API_URL}/product-type/${mid}`, {
        withXSRFToken: true,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: `application/json`,
        },
      });
      const data = response;
      setProductTypeFormData({
        productId: data.productId || '',
        name: data.name || '',
        description: data.description || '',
        uoM: data.uoM || '',
        pricingUnit: data.pricingUnit || '',
        id: data.id || null,
      });
    } catch (error) {
      notify.apiError(error, 'fetch product type details');
    }
  };

  const handleUpdDataProductType = async mid => {
    try {
      await axios.put(
        `${api.API_URL}/product-type/${mid}`,
        selectedFieldsProductType,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(MESSAGES.updated);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      const filters = {
        productId: selectedProduct,
        searchText: search,
      };
      fetchData(currentPage, rowsPerPage, token, filters);
    }
  };

  // Update one field and clear its error as soon as the user edits it.
  const setFormField = (field, value) => {
    setProductTypeFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSaveProductType = async () => {
    const nextErrors = validateForm(productTypeFormData, PRODUCT_TYPE_SCHEMA);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) {
      focusFirstError(nextErrors, PRODUCT_TYPE_ORDER);
      return;
    }
    if (productTypeFormData.id !== null) {
      await handleUpdDataProductType(productTypeFormData.id);
    } else {
      await handleAddDataProductType();
    }
    setModalProductType(false);
  };

  const productOptions = products.map(product => ({
    value: product.id,
    label: product.name,
  }));
  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterChange({ target: { value: '' } }),
    },
    selectedProduct && {
      key: 'product',
      label: 'Product',
      value:
        productOptions.find(o => o.value === selectedProduct)?.label ??
        selectedProduct,
      onRemove: () => handleProductFilterChange(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this product type?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.name}
          confirmLabel='Delete product type'
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{
                      label: 'Product Type',
                      to: '/apps-product-type-view/view',
                    }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-stack-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Product Type</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the product types of each product, with their
                        unit of measure and pricing unit.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(currentPage, rowsPerPage, token, {
                            productId: selectedProduct,
                            searchText: search,
                          })
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Product Type
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search product types...'
                      ariaLabel='Search product types'
                    />
                    <ListFilterSelect
                      label='Product'
                      value={selectedProduct}
                      onChange={handleProductFilterChange}
                      allLabel='All Products'
                      options={productOptions}
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearAllFilters}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} product types`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='name'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No product type found'
                          emptyHint='No product types have been registered yet. Product types added here are listed under their product.'
                          filteredHint='No product type matches the current filters.'
                          hasFilters={hasFilters}
                          onClear={clearAllFilters}
                          action={{
                            label: 'Create New Product Type',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalProductType}
            role='dialog'
            autoFocus={true}
            centered
            id='addProductTypeModal'
            toggle={productTypeModal}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalProductType(!modalProductType);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {productTypeFormData.id !== null
                  ? 'Edit Product Type'
                  : 'Add Product Type'}
              </span>
            </ModalHeader>

            <ModalBody>
              <RequiredNote />
              <FormGrid columns={1}>
                <SelectField
                  label='Product'
                  name='productId'
                  required
                  placeholder='Select product'
                  options={products.map(product => ({
                    value: product.id,
                    label: product.name,
                  }))}
                  value={productTypeFormData.productId}
                  error={errors.productId}
                  onChange={value => setFormField('productId', value ?? '')}
                />
                <TextField
                  label='Product type name'
                  name='name'
                  required
                  value={productTypeFormData.name}
                  error={errors.name}
                  onChange={e => setFormField('name', e.target.value)}
                />
                <SelectField
                  label='UoM'
                  name='uoM'
                  options={UoMType}
                  value={productTypeFormData.uoM}
                  onChange={value => setFormField('uoM', value ?? '')}
                />
                <SelectField
                  label='Pricing unit'
                  name='pricingUnit'
                  options={PricingUnitType}
                  value={productTypeFormData.pricingUnit}
                  onChange={value => setFormField('pricingUnit', value ?? '')}
                />
                <TextAreaField
                  label='Description'
                  name='description'
                  value={productTypeFormData.description}
                  onChange={e => setFormField('description', e.target.value)}
                />
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => {
                  setModalProductType(!modalProductType);
                }}
              >
                Close
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={handleSaveProductType}
              >
                {productTypeFormData.id !== null ? 'Update' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProductTypeList;
